import { useRef, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Pencil,
  UserX,
  FileText,
  Download,
  Trash2,
  Upload,
  Plus,
  Star,
  CheckCircle2,
  Circle,
  Eye,
} from 'lucide-react';
import {
  useEmployee,
  useEmployeeDocuments,
  useInitDocumentUpload,
  useDeleteDocument,
  useDocumentDownloadUrl,
  useConfirmDocument,
  useUpdateDocument,
  useDeleteEmployee,
  useEvaluations,
  useCreateEvaluation,
  useDeleteEvaluation,
  useTrainings,
  useCreateTraining,
  useDeleteTraining,
  useUpdateCompliance,
} from '@/lib/queries';
import { useAuthStore } from '@/store/auth.store';
import { initials } from '@isoft-people/utils';

const TABS = [
  'Datos Generales',
  'Contacto',
  'Perfil del Puesto',
  'Desempeño',
  'Cumplimiento',
  'Documentos',
] as const;

type Tab = (typeof TABS)[number];

const DOC_CATEGORIES = [
  { key: 'DPI', label: 'DPI' },
  { key: 'PASSPORT', label: 'Pasaporte' },
  { key: 'UNIVERSITY_DEGREE', label: 'Título Universitario' },
  { key: 'POLICE_RECORD', label: 'Antecedentes Policíacos' },
  { key: 'CRIMINAL_RECORD', label: 'Antecedentes Penales' },
  { key: 'RESIDENCE_PROOF', label: 'Constancia de Residencia' },
  { key: 'CV', label: 'Currículum Vitae' },
  { key: 'CONTRACT', label: 'Contrato' },
  { key: 'TAX_ID', label: 'NIT / Tax ID' },
  { key: 'OTHER', label: 'Otro' },
];

const STATUS_STYLES: Record<string, string> = {
  VALID: 'text-accent bg-accent-light',
  PENDING: 'text-warning bg-yellow-50',
  EXPIRED: 'text-error bg-red-50',
  REJECTED: 'text-error bg-red-50',
};

const RATING_LABELS: Record<string, string> = {
  OUTSTANDING: 'Sobresaliente',
  EXCEEDS: 'Excede',
  MEETS: 'Cumple',
  NEEDS_IMPROVEMENT: 'Necesita mejora',
  UNSATISFACTORY: 'Insatisfactorio',
};

const RATING_COLORS: Record<string, string> = {
  OUTSTANDING: 'bg-accent-light text-accent',
  EXCEEDS: 'bg-blue-50 text-info',
  MEETS: 'bg-primary-subtle text-primary',
  NEEDS_IMPROVEMENT: 'bg-yellow-50 text-warning',
  UNSATISFACTORY: 'bg-red-50 text-error',
};

export function EmployeeDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { data: e, isLoading } = useEmployee(id);
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === 'ADMIN' || role === 'HR';
  const [tab, setTab] = useState<Tab>('Datos Generales');
  const deactivate = useDeleteEmployee();

  const handleDeactivate = async () => {
    if (!e) return;
    if (
      !confirm(
        `¿Desactivar a ${e.firstName} ${e.lastName}?\n\nSu estado pasará a TERMINATED y no podrá iniciar sesión. Podrás reactivarlo después desde Editar.`,
      )
    )
      return;
    try {
      await deactivate.mutateAsync(e.id);
      navigate('/colaboradores');
    } catch (err: any) {
      alert(err?.response?.data?.error?.message ?? 'No se pudo desactivar');
    }
  };

  if (isLoading)
    return <p className="py-10 text-center text-font-secondary">Cargando...</p>;
  if (!e)
    return (
      <p className="py-10 text-center text-font-secondary">
        Colaborador no encontrado.
      </p>
    );

  const fullName = `${e.firstName} ${e.lastName}`;

  return (
    <div className="flex flex-col gap-6">
      <Link
        to="/colaboradores"
        className="inline-flex items-center gap-2 text-sm font-medium text-font-secondary hover:text-primary"
      >
        <ArrowLeft size={16} /> Volver a colaboradores
      </Link>

      {/* Header card */}
      <div className="flex items-center gap-5 rounded-lg border border-border bg-white p-6 shadow-sm">
        {e.photoUrl ? (
          <img
            src={e.photoUrl}
            alt={fullName}
            className="h-20 w-20 flex-shrink-0 rounded-full object-cover"
          />
        ) : (
          <div className="flex h-20 w-20 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-primary to-primary-light text-2xl font-semibold text-white">
            {initials(fullName)}
          </div>
        )}
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-font-primary">{fullName}</h1>
          <p className="mt-1 text-sm text-font-secondary">
            {e.position?.title ?? '–'} · {e.country?.nameEs ?? '–'}
          </p>
          <div className="mt-2 flex items-center gap-2">
            {e.status === 'TERMINATED' ? (
              <span className="inline-flex items-center gap-1 rounded-full bg-red-50 px-2 py-0.5 text-[11px] font-semibold text-error">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                Dado de baja
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 rounded-full bg-accent-light px-2 py-0.5 text-[11px] font-semibold text-accent">
                <span className="h-1.5 w-1.5 rounded-full bg-current" />
                Activo
              </span>
            )}
            <span className="text-xs text-font-tertiary">
              Desde {new Date(e.hireDate).toLocaleDateString('es')} · #
              {e.employeeNumber}
            </span>
          </div>
        </div>

        {canEdit && (
          <div className="flex items-center gap-2">
            <Link
              to={`/colaboradores/${e.id}/editar`}
              className="flex h-10 items-center gap-2 rounded-md border border-border bg-white px-4 text-sm font-semibold text-font-primary hover:bg-bg"
            >
              <Pencil size={14} /> Editar
            </Link>
            {e.status !== 'TERMINATED' && (
              <button
                onClick={handleDeactivate}
                disabled={deactivate.isPending}
                className="flex h-10 items-center gap-2 rounded-md border border-error bg-white px-4 text-sm font-semibold text-error hover:bg-red-50 disabled:opacity-60"
              >
                <UserX size={14} /> {deactivate.isPending ? 'Desactivando...' : 'Desactivar'}
              </button>
            )}
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="rounded-lg border border-border bg-white shadow-sm">
        <div className="flex gap-1 border-b border-border px-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`relative px-4 py-4 text-sm font-semibold transition ${
                tab === t
                  ? 'text-primary'
                  : 'text-font-secondary hover:text-font-primary'
              }`}
            >
              {t}
              {tab === t && (
                <span className="absolute inset-x-0 bottom-0 h-0.5 rounded-t bg-primary" />
              )}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === 'Datos Generales' && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
              <Field label="Nombre completo" value={fullName} />
              <Field label="Número de empleado" value={e.employeeNumber} />
              <Field label="Puesto" value={e.position?.title} />
              <Field label="Departamento" value={e.department?.name} />
              <Field label="País" value={e.country?.nameEs} />
              <Field
                label="Fecha de ingreso"
                value={new Date(e.hireDate).toLocaleDateString('es', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric',
                })}
              />
              <Field
                label="Jefe directo"
                value={
                  e.manager ? `${e.manager.firstName} ${e.manager.lastName}` : '–'
                }
              />
              <Field label="Modalidad" value={e.workMode ?? 'Presencial'} />
            </div>
          )}

          {tab === 'Contacto' && (
            <div className="grid grid-cols-1 gap-x-8 gap-y-5 md:grid-cols-2">
              <Field label="Correo corporativo" value={e.corporateEmail} />
              <Field label="Correo personal" value={e.personalEmail} />
              <Field label="Teléfono" value={e.phone} />
              <Field label="Dirección" value={e.address} />
              <Field label="Contacto de emergencia" value={e.emergencyContact} />
              <Field label="Teléfono de emergencia" value={e.emergencyPhone} />
            </div>
          )}

          {tab === 'Perfil del Puesto' && (
            <div className="space-y-4">
              <Field
                label="Función principal"
                value={e.mainFunction ?? 'Sin definir'}
              />
              {[e.responsibility1, e.responsibility2, e.responsibility3]
                .filter(Boolean)
                .map((r, i) => (
                  <p key={i} className="text-sm text-font-primary">
                    • {r}
                  </p>
                ))}
            </div>
          )}

          {tab === 'Desempeño' && <PerformanceTab employeeId={e.id} />}
          {tab === 'Cumplimiento' && <ComplianceTab employee={e} />}
          {tab === 'Documentos' && <DocumentsTab employeeId={e.id} />}
        </div>
      </div>
    </div>
  );
}

function Field({ label, value }: { label: string; value?: string | null }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-font-tertiary">
        {label}
      </p>
      <p className="mt-1 text-sm text-font-primary">{value || '–'}</p>
    </div>
  );
}

// ==================== DOCUMENTS ====================
function DocumentsTab({ employeeId }: { employeeId: string }) {
  const { data, isLoading, refetch } = useEmployeeDocuments(employeeId);
  const initUpload = useInitDocumentUpload();
  const confirmUpload = useConfirmDocument();
  const updateDoc = useUpdateDocument();
  const del = useDeleteDocument();
  const download = useDocumentDownloadUrl();
  const role = useAuthStore((s) => s.user?.role);
  const canManage = role === 'ADMIN' || role === 'HR';
  const fileRef = useRef<HTMLInputElement>(null);
  const [category, setCategory] = useState('DPI');
  const [uploading, setUploading] = useState(false);

  const handleFilePicked = async (file: File) => {
    if (!file) return;
    setUploading(true);
    try {
      const { document: newDoc, uploadUrl } = await initUpload.mutateAsync({
        employeeId,
        payload: {
          category,
          name: DOC_CATEGORIES.find((c) => c.key === category)?.label ?? file.name,
          fileName: file.name,
          mimeType: file.type || 'application/octet-stream',
          size: file.size,
        },
      });
      const putRes = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
      });
      if (!putRes.ok) throw new Error('S3 upload failed');
      // Confirmar: marca el documento como VALID ahora que la subida terminó
      try {
        await confirmUpload.mutateAsync({ id: newDoc.id, status: 'VALID' });
      } catch (err) {
        console.warn('No se pudo confirmar el documento', err);
      }
      await refetch();
    } catch (err) {
      alert('No se pudo subir el documento');
      console.error(err);
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  const handleDownload = async (id: string) => {
    const { url } = await download.mutateAsync({ id, inline: false });
    window.open(url, '_blank');
  };

  const handleView = async (id: string) => {
    const { url } = await download.mutateAsync({ id, inline: true });
    window.open(url, '_blank');
  };

  const handleChangeStatus = async (id: string, status: string) => {
    await updateDoc.mutateAsync({ id, payload: { status } });
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between gap-3">
        <h3 className="text-base font-semibold text-font-primary">
          Documentos del Colaborador
        </h3>
        <div className="flex items-center gap-2">
          <select
            value={category}
            onChange={(e) => setCategory(e.target.value)}
            className="h-10 rounded-md border border-border bg-white px-3 text-sm text-font-primary"
          >
            {DOC_CATEGORIES.map((c) => (
              <option key={c.key} value={c.key}>
                {c.label}
              </option>
            ))}
          </select>
          <input
            ref={fileRef}
            type="file"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleFilePicked(f);
            }}
          />
          <button
            disabled={uploading}
            onClick={() => fileRef.current?.click()}
            className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
          >
            <Upload size={14} /> {uploading ? 'Subiendo...' : 'Subir documento'}
          </button>
        </div>
      </div>
      {isLoading && <p className="text-sm text-font-secondary">Cargando...</p>}
      {!isLoading && (data?.items?.length ?? 0) === 0 && (
        <p className="py-6 text-center text-sm text-font-tertiary">
          Aún no hay documentos cargados para este colaborador.
        </p>
      )}
      {(data?.items?.length ?? 0) > 0 && (
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-border text-left text-[11px] font-semibold uppercase tracking-wide text-font-secondary">
              <th className="py-3">Documento</th>
              <th className="py-3">Fecha</th>
              <th className="py-3">Tamaño</th>
              <th className="py-3">Estado</th>
              <th className="py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {data!.items.map((d: any) => (
              <tr key={d.id} className="border-b border-border-light">
                <td className="py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-md bg-red-50 text-error">
                      <FileText size={16} />
                    </div>
                    <div>
                      <p className="font-semibold text-font-primary">{d.name}</p>
                      <p className="text-xs text-font-tertiary">
                        {d.fileName} ·{' '}
                        {DOC_CATEGORIES.find((c) => c.key === d.category)?.label ??
                          d.category}
                      </p>
                    </div>
                  </div>
                </td>
                <td className="py-4 text-font-secondary">
                  {new Date(d.uploadedAt).toLocaleDateString('es')}
                </td>
                <td className="py-4 text-font-secondary">
                  {(d.size / 1024).toFixed(0)} KB
                </td>
                <td className="py-4">
                  {canManage ? (
                    <select
                      value={d.status}
                      onChange={(ev) => handleChangeStatus(d.id, ev.target.value)}
                      className={`rounded-md border border-border px-2 py-1 text-[11px] font-semibold ${
                        STATUS_STYLES[d.status] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      <option value="VALID">Válido</option>
                      <option value="PENDING">Pendiente</option>
                      <option value="EXPIRED">Vencido</option>
                      <option value="REJECTED">Rechazado</option>
                    </select>
                  ) : (
                    <span
                      className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                        STATUS_STYLES[d.status] ?? 'bg-gray-100 text-gray-600'
                      }`}
                    >
                      {d.status}
                    </span>
                  )}
                </td>
                <td className="py-4">
                  <div className="flex items-center justify-end gap-1">
                    <button
                      onClick={() => handleView(d.id)}
                      title="Ver"
                      className="flex h-8 w-8 items-center justify-center rounded-md text-font-secondary hover:bg-bg"
                    >
                      <Eye size={14} />
                    </button>
                    <button
                      onClick={() => handleDownload(d.id)}
                      title="Descargar"
                      className="flex h-8 w-8 items-center justify-center rounded-md text-font-secondary hover:bg-bg"
                    >
                      <Download size={14} />
                    </button>
                    {canManage && (
                      <button
                        onClick={() => {
                          if (confirm('¿Eliminar este documento?')) del.mutate(d.id);
                        }}
                        title="Eliminar"
                        className="flex h-8 w-8 items-center justify-center rounded-md text-error hover:bg-red-50"
                      >
                        <Trash2 size={14} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ==================== PERFORMANCE ====================
function PerformanceTab({ employeeId }: { employeeId: string }) {
  const { data, isLoading } = useEvaluations(employeeId);
  const create = useCreateEvaluation();
  const del = useDeleteEvaluation();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    evaluationDate: new Date().toISOString().slice(0, 10),
    rating: 'MEETS',
    score: '',
    periodLabel: '',
    strengths: '',
    improvementAreas: '',
    developmentPlan: '',
    notes: '',
  });

  const handleSave = async () => {
    await create.mutateAsync({
      employeeId,
      payload: {
        evaluationDate: form.evaluationDate,
        rating: form.rating,
        score: form.score ? Number(form.score) : undefined,
        periodLabel: form.periodLabel || undefined,
        strengths: form.strengths || undefined,
        improvementAreas: form.improvementAreas || undefined,
        developmentPlan: form.developmentPlan || undefined,
        notes: form.notes || undefined,
      },
    });
    setOpen(false);
  };

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-base font-semibold text-font-primary">
          Evaluaciones de Desempeño
        </h3>
        <button
          onClick={() => setOpen(true)}
          className="flex h-10 items-center gap-2 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark"
        >
          <Plus size={14} /> Nueva evaluación
        </button>
      </div>

      {isLoading && <p className="text-sm text-font-secondary">Cargando...</p>}
      {!isLoading && (data?.items?.length ?? 0) === 0 && (
        <p className="py-6 text-center text-sm text-font-tertiary">
          Sin evaluaciones registradas.
        </p>
      )}

      <div className="space-y-3">
        {data?.items?.map((ev: any) => (
          <div
            key={ev.id}
            className="rounded-lg border border-border p-4 hover:border-primary"
          >
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <Star size={14} className="text-warning" />
                  <span
                    className={`rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      RATING_COLORS[ev.rating] ?? ''
                    }`}
                  >
                    {RATING_LABELS[ev.rating] ?? ev.rating}
                  </span>
                  {ev.score != null && (
                    <span className="text-xs font-semibold text-font-secondary">
                      Puntaje: {ev.score}
                    </span>
                  )}
                  <span className="text-xs text-font-tertiary">
                    {new Date(ev.evaluationDate).toLocaleDateString('es')}
                    {ev.periodLabel ? ` · ${ev.periodLabel}` : ''}
                  </span>
                </div>
                {ev.strengths && (
                  <p className="mt-2 text-sm text-font-primary">
                    <strong>Fortalezas:</strong> {ev.strengths}
                  </p>
                )}
                {ev.improvementAreas && (
                  <p className="mt-1 text-sm text-font-primary">
                    <strong>Áreas de mejora:</strong> {ev.improvementAreas}
                  </p>
                )}
                {ev.developmentPlan && (
                  <p className="mt-1 text-sm text-font-primary">
                    <strong>Plan de desarrollo:</strong> {ev.developmentPlan}
                  </p>
                )}
                {ev.notes && (
                  <p className="mt-1 text-sm text-font-secondary">{ev.notes}</p>
                )}
              </div>
              <button
                onClick={() => {
                  if (confirm('¿Eliminar evaluación?')) del.mutate(ev.id);
                }}
                className="text-error hover:text-error"
              >
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4">
          <div className="w-full max-w-lg rounded-lg bg-white p-6 shadow-lg">
            <h3 className="mb-4 text-lg font-semibold text-font-primary">
              Nueva evaluación
            </h3>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs text-font-secondary">Fecha</label>
                <input
                  type="date"
                  value={form.evaluationDate}
                  onChange={(e) =>
                    setForm({ ...form, evaluationDate: e.target.value })
                  }
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Calificación</label>
                <select
                  value={form.rating}
                  onChange={(e) => setForm({ ...form, rating: e.target.value })}
                  className="input h-10 w-full"
                >
                  {Object.entries(RATING_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>
                      {v}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="text-xs text-font-secondary">Puntaje (0-100)</label>
                <input
                  type="number"
                  min="0"
                  max="100"
                  value={form.score}
                  onChange={(e) => setForm({ ...form, score: e.target.value })}
                  className="input h-10 w-full"
                />
              </div>
              <div>
                <label className="text-xs text-font-secondary">Período</label>
                <input
                  type="text"
                  value={form.periodLabel}
                  onChange={(e) => setForm({ ...form, periodLabel: e.target.value })}
                  className="input h-10 w-full"
                  placeholder="Q1 2026"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-font-secondary">Fortalezas</label>
                <textarea
                  value={form.strengths}
                  onChange={(e) => setForm({ ...form, strengths: e.target.value })}
                  className="input min-h-[60px] w-full"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-font-secondary">Áreas de mejora</label>
                <textarea
                  value={form.improvementAreas}
                  onChange={(e) =>
                    setForm({ ...form, improvementAreas: e.target.value })
                  }
                  className="input min-h-[60px] w-full"
                />
              </div>
              <div className="col-span-2">
                <label className="text-xs text-font-secondary">Plan de desarrollo</label>
                <textarea
                  value={form.developmentPlan}
                  onChange={(e) =>
                    setForm({ ...form, developmentPlan: e.target.value })
                  }
                  className="input min-h-[60px] w-full"
                />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setOpen(false)}
                className="h-10 rounded-md border border-border px-4 text-sm font-semibold text-font-secondary hover:bg-bg"
              >
                Cancelar
              </button>
              <button
                onClick={handleSave}
                disabled={create.isPending}
                className="h-10 rounded-md bg-primary px-4 text-sm font-semibold text-white hover:bg-primary-dark disabled:opacity-60"
              >
                {create.isPending ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ==================== COMPLIANCE ====================
function ComplianceTab({ employee }: { employee: any }) {
  const update = useUpdateCompliance();
  const { data: trainings } = useTrainings(employee.id);
  const createTr = useCreateTraining();
  const delTr = useDeleteTraining();
  const [flags, setFlags] = useState({
    codeOfConductSigned: employee.codeOfConductSigned ?? false,
    policiesSigned: employee.policiesSigned ?? false,
    trainingsCompleted: employee.trainingsCompleted ?? false,
    complianceNotes: employee.complianceNotes ?? '',
  });
  const [trForm, setTrForm] = useState({
    name: '',
    category: '',
    completedDate: '',
    hours: '',
  });

  const toggleFlag = async (key: keyof typeof flags) => {
    const next = { ...flags, [key]: !flags[key] };
    setFlags(next);
    await update.mutateAsync({ employeeId: employee.id, payload: next });
  };

  const saveNotes = async () => {
    await update.mutateAsync({ employeeId: employee.id, payload: flags });
  };

  const addTraining = async () => {
    if (!trForm.name) return;
    await createTr.mutateAsync({
      employeeId: employee.id,
      payload: {
        name: trForm.name,
        category: trForm.category || undefined,
        completedDate: trForm.completedDate || undefined,
        hours: trForm.hours ? Number(trForm.hours) : undefined,
      },
    });
    setTrForm({ name: '', category: '', completedDate: '', hours: '' });
  };

  const flagItems = [
    { key: 'codeOfConductSigned' as const, label: 'Código de conducta firmado' },
    { key: 'policiesSigned' as const, label: 'Políticas internas firmadas' },
    { key: 'trainingsCompleted' as const, label: 'Capacitaciones completadas' },
  ];

  return (
    <div className="space-y-6">
      <div>
        <h3 className="mb-3 text-base font-semibold text-font-primary">
          Cumplimiento Normativo
        </h3>
        <div className="space-y-2">
          {flagItems.map((it) => (
            <button
              key={it.key}
              onClick={() => toggleFlag(it.key)}
              className="flex w-full items-center gap-3 rounded-md border border-border p-3 text-left hover:border-primary"
            >
              {flags[it.key] ? (
                <CheckCircle2 size={18} className="text-accent" />
              ) : (
                <Circle size={18} className="text-font-tertiary" />
              )}
              <span className="text-sm font-semibold text-font-primary">{it.label}</span>
            </button>
          ))}
        </div>
        <div className="mt-4">
          <label className="text-xs text-font-secondary">Notas</label>
          <textarea
            value={flags.complianceNotes}
            onChange={(e) => setFlags({ ...flags, complianceNotes: e.target.value })}
            onBlur={saveNotes}
            className="input mt-1 min-h-[80px] w-full"
          />
        </div>
      </div>

      <div>
        <h3 className="mb-3 text-base font-semibold text-font-primary">
          Capacitaciones
        </h3>
        <div className="mb-3 grid grid-cols-12 gap-2">
          <input
            placeholder="Nombre"
            value={trForm.name}
            onChange={(e) => setTrForm({ ...trForm, name: e.target.value })}
            className="input col-span-4 h-10"
          />
          <input
            placeholder="Categoría"
            value={trForm.category}
            onChange={(e) => setTrForm({ ...trForm, category: e.target.value })}
            className="input col-span-3 h-10"
          />
          <input
            type="date"
            value={trForm.completedDate}
            onChange={(e) => setTrForm({ ...trForm, completedDate: e.target.value })}
            className="input col-span-2 h-10"
          />
          <input
            type="number"
            placeholder="Horas"
            value={trForm.hours}
            onChange={(e) => setTrForm({ ...trForm, hours: e.target.value })}
            className="input col-span-2 h-10"
          />
          <button
            onClick={addTraining}
            className="col-span-1 flex h-10 items-center justify-center rounded-md bg-primary text-white hover:bg-primary-dark"
          >
            <Plus size={14} />
          </button>
        </div>
        <div className="space-y-2">
          {trainings?.items?.map((t: any) => (
            <div
              key={t.id}
              className="flex items-center justify-between rounded-md border border-border p-3"
            >
              <div>
                <p className="text-sm font-semibold text-font-primary">{t.name}</p>
                <p className="text-xs text-font-tertiary">
                  {t.category ?? '–'} ·{' '}
                  {t.completedDate
                    ? new Date(t.completedDate).toLocaleDateString('es')
                    : 'Sin fecha'}{' '}
                  · {t.hours ?? 0} h
                </p>
              </div>
              <button
                onClick={() => {
                  if (confirm('¿Eliminar capacitación?')) delTr.mutate(t.id);
                }}
                className="text-error"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
          {(trainings?.items?.length ?? 0) === 0 && (
            <p className="py-4 text-center text-sm text-font-tertiary">
              Sin capacitaciones registradas.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
